import { Injectable, inject } from '@angular/core';

import { EdsPromoTokenType } from '@bpos';
import { SlipState } from '@bpos/v1/my-bets';
import { CRMTokenImages, CrmConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { MoneyService } from '@frontend/sports/common/core/feature/money';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { CRMTokenImage } from '@frontend/sports/types/models/crm-tokens';
import { groupBy, keys, sumBy } from 'lodash-es';
import { BetAndGetEdsToken } from 'packages/sports/common/betslip/modules/reward-tokens/reward-tokens.model';

import { MyBetsBetSlip, SignPostingReward, TokenType } from '../my-bets-base/models/my-bets-viewmodels';
import { MyBetsUtilService } from '../my-bets/services/my-bets-util.service';
import { PayoutType, RewardTokenType, RewardTriggerType, SignPostPayoutType, SignPostingOfferType } from '../tokens-base/token-base.models';

export interface ISignpostingAwardBanner {
    text: string;
    cssClass: string;
}

export interface ITokenSignposting {
    iconCssClass: string;
    title: string;
    description?: string;
}

@Injectable({ providedIn: 'root' })
export class PostbetSignpostingService {
    private readonly signPostingRewardOfferTypes = [SignPostingOfferType.Promo.toString(), SignPostingOfferType.Eds.toString()];
    private sitecore = inject(Sitecore);
    private crmConfig = inject(CrmConfig);
    private moneyService = inject(MoneyService);
    private crmTokenimage = inject(CRMTokenImages);
    private myBetsUtilService = inject(MyBetsUtilService);
    private readonly enableRedesignedBetCard = this.myBetsUtilService.isBetCardRedesigned();

    getAwardBanners(rewards: SignPostingReward[]): ISignpostingAwardBanner[] {
        if (!this.crmConfig.signposting.isEnabled || !rewards) {
            return [];
        }

        return this.groupRewards(rewards.filter((r) => this.signPostingRewardOfferTypes.includes(r.offerType)))
            .map((reward) => this.mapReward(reward))
            .filter(isDefined);
    }

    getBetAndGetToken(betslip: MyBetsBetSlip): ITokenSignposting | undefined {
        if (
            !(betslip.edsPromoTokens.length === 1 && betslip.edsPromoTokens[0].tokenType === EdsPromoTokenType.BetAndGet) ||
            (betslip.isCachedOut && betslip.signPostingRewards.length === 0)
        ) {
            return;
        }

        if (betslip.signPostingRewards.length === 0 && betslip.state === SlipState.Open && betslip.placeBetSignPosting) {
            return this.getBetPlacementDescription(betslip.placeBetSignPosting as BetAndGetEdsToken);
        } else if (betslip.signPostingRewards.length > 0) {
            return this.getBetAwardedDescription(betslip.signPostingRewards[0].type, this.awardedValue(betslip.signPostingRewards[0].value));
        } else if (this.enableRedesignedBetCard) {
            if (betslip.edsPromoTokens.length === 1 && betslip.edsPromoTokens[0].tokenType === EdsPromoTokenType.BetAndGet) {
                return this.betAndGetDescription(betslip.placeBetSignPosting as BetAndGetEdsToken);
            }
        }

        return;
    }

    private getBetPlacementDescription(token: BetAndGetEdsToken): ITokenSignposting {
        let displayPayoutType = '';
        let payoutValue = '';
        const triggerTypeTitle =
            token.triggerType === RewardTriggerType.BetPlacement
                ? this.sitecore.crmTokens.betAndGet.betslipSummary.BetPlacementTemplate
                : this.sitecore.crmTokens.betAndGet.betslipSummary.BetSettlementTemplate;

        const rewardAwarded = this.getRewardAwardedValue(token);

        switch (token.payoutType) {
            case PayoutType.Cash:
                displayPayoutType = this.sitecore.crmTokens.betAndGet.betslipSummary.CashPayoutTemplate;
                payoutValue = rewardAwarded.rewardPayout;
                break;
            case PayoutType.Bonus:
                displayPayoutType = this.sitecore.crmTokens.betAndGet.betslipSummary.BonusPayoutTemplate;
                payoutValue = rewardAwarded.rewardPayout;
                break;
            case PayoutType.FreeBet:
                displayPayoutType = this.sitecore.crmTokens.betAndGet.betslipSummary.FreebetPayoutTemplate;
                payoutValue = rewardAwarded.rewardPayout;
                break;
            case PayoutType.OddsBoostToken:
                displayPayoutType = this.sitecore.crmTokens.betAndGet.betslipSummary.OddsBoostPayoutTemplate;
                break;
            case PayoutType.RiskFreeToken:
                displayPayoutType = this.sitecore.crmTokens.betAndGet.betslipSummary.RiskFreePayoutTemplate;
                break;
        }

        if (token.triggerType === RewardTriggerType.BetPlacement) {
            let displayPayoutTemplate = this.enableRedesignedBetCard
                ? this.sitecore.crmPostBetSignposting.messages.BetAndGetPromoAwarded
                : this.sitecore.crmPostBetSignposting.messages.BetAndGetAwarded;
            if (rewardAwarded.percentagePayout) {
                displayPayoutTemplate = this.enableRedesignedBetCard
                    ? this.sitecore.crmPostBetSignposting.messages.BetAndGetPromoPercentageAwarded
                    : this.sitecore.crmPostBetSignposting.messages.BetAndGetPercentageAwarded;
            }

            return {
                iconCssClass: this.iconClasses(token.payoutType),
                title: interpolateDoubleBracesString(displayPayoutTemplate, {
                    payoutType: displayPayoutType,
                    value: payoutValue,
                }),
            };
        } else {
            let displayPayoutTemplate = this.sitecore.crmPostBetSignposting.messages.BetAndGetPlaced;
            if (rewardAwarded.percentagePayout) {
                displayPayoutTemplate = this.sitecore.crmPostBetSignposting.messages.BetAndGetPercentagePlaced;
            }

            return {
                iconCssClass: this.iconClasses(token.payoutType),
                title: interpolateDoubleBracesString(displayPayoutTemplate, {
                    payoutType: displayPayoutType,
                    value: payoutValue,
                    triggerType: triggerTypeTitle,
                }),
                description: interpolateDoubleBracesString(this.sitecore.crmPostBetSignposting.messages.BetAndGetCashOutDescription, {
                    payoutType: token.payoutType,
                }),
            };
        }
    }

    private betAndGetDescription(token: BetAndGetEdsToken): ITokenSignposting {
        return {
            iconCssClass: this.iconClasses(token.payoutType),
            title: interpolateDoubleBracesString(this.sitecore.crmTokens.linearRewardTokens.MyBetsBetAndGetPromoDetailsTemplate, {
                payoutType: token.payoutType,
                amount: this.moneyService.transform(token.payoutDetails.value, true),
            }),
        };
    }

    private getRewardAwardedValue(token: BetAndGetEdsToken): { rewardPayout: string; percentagePayout: boolean } {
        if (!token.payoutDetails.value && !token.payoutDetails.percentage) {
            return { rewardPayout: '', percentagePayout: false };
        }
        if (token.payoutDetails.value) {
            return { rewardPayout: this.awardedValue(token.payoutDetails.value), percentagePayout: false };
        } else {
            return { rewardPayout: token.payoutDetails.percentage!.toString(), percentagePayout: true };
        }
    }

    private awardedValue(money: number): string {
        return this.moneyService.transform(money, true);
    }

    getRewardTokenImage(rewardTokenType: TokenType | undefined): CRMTokenImage | undefined {
        let rewardTokenImageSrc;
        switch (rewardTokenType) {
            case TokenType.FreeBetToken:
                rewardTokenImageSrc = this.findRewardTokenFromCrm(RewardTokenType.FreeBet);
                break;
            case TokenType.OddsBoostToken:
                rewardTokenImageSrc = this.findRewardTokenFromCrm(RewardTokenType.OddsBoost);
                break;
            case TokenType.BetAndGetToken:
                rewardTokenImageSrc = this.findRewardTokenFromCrm(RewardTokenType.BetAndGet);
                break;
            case TokenType.RiskFreeToken:
                rewardTokenImageSrc = this.findRewardTokenFromCrm(RewardTokenType.RiskFreeBet);
                break;
        }
        return rewardTokenImageSrc;
    }

    private findRewardTokenFromCrm = (rewardTokenType: RewardTokenType) => this.crmTokenimage.crmTokenImages.find((x) => x.name === rewardTokenType);

    private getBetAwardedDescription(payoutType: string, reward: string): ITokenSignposting {
        let signPostPayout = '';

        switch (payoutType) {
            case SignPostPayoutType.Cash:
            case PayoutType.Cash:
                signPostPayout = this.sitecore.crmTokens.betAndGet.betslipSummary.CashPayoutTemplate;
                break;
            case SignPostPayoutType.Bonus:
            case PayoutType.Bonus:
                signPostPayout = this.sitecore.crmTokens.betAndGet.betslipSummary.BonusPayoutTemplate;
                break;
            case SignPostPayoutType.FreeBet:
            case PayoutType.FreeBet:
                signPostPayout = this.sitecore.crmTokens.betAndGet.betslipSummary.FreebetPayoutTemplate;

                break;
            case SignPostPayoutType.OddsBoostToken:
            case PayoutType.OddsBoostToken:
                signPostPayout = this.sitecore.crmTokens.betAndGet.betslipSummary.OddsBoostPayoutTemplate;
                reward = '';
                break;
            case SignPostPayoutType.RiskFreeToken:
            case PayoutType.RiskFreeToken:
                reward = '';
                signPostPayout = this.sitecore.crmTokens.betAndGet.betslipSummary.RiskFreePayoutTemplate;
                break;
        }

        return {
            iconCssClass: this.iconClasses(payoutType),
            title: interpolateDoubleBracesString(
                this.enableRedesignedBetCard
                    ? this.sitecore.crmPostBetSignposting.messages.BetAndGetPromoAwarded
                    : this.sitecore.crmPostBetSignposting.messages.BetAndGetAwarded,
                {
                    payoutType: signPostPayout,
                    value: reward,
                },
            ),
        };
    }

    private iconClasses(payoutType: string): string {
        const iconClass = 'signposting-reward-tokens-card__icon';
        switch (payoutType) {
            case PayoutType.Cash:
            case SignPostPayoutType.Cash:
                return this.crmConfig.tokens.icons.cash + ' ' + iconClass + '--cash';
            case PayoutType.FreeBet:
            case SignPostPayoutType.FreeBet:
                return this.crmConfig.tokens.icons.freeBet + ' ' + iconClass + '--freebet';
            case PayoutType.OddsBoostToken:
            case SignPostPayoutType.OddsBoostToken:
                return this.crmConfig.tokens.icons.oddsBoost + ' ' + iconClass + '--oddsboost';
            case PayoutType.RiskFreeToken:
            case SignPostPayoutType.RiskFreeToken:
                return this.crmConfig.tokens.icons.riskFree + ' ' + iconClass + '--riskfree';
            case PayoutType.Bonus:
            case SignPostPayoutType.Bonus:
                return this.crmConfig.tokens.icons.bonus + ' ' + iconClass + '--bonus';

            default:
                return '';
        }
    }

    private mapReward(reward: SignPostingReward): ISignpostingAwardBanner | undefined {
        const mapping = this.crmConfig.signposting.awardTypeMapping[reward.type.toLowerCase()];
        if (mapping && mapping.sitecoreReference) {
            const rewardType = this.sitecore.crmPostBetSignposting.rewardTypes[mapping.sitecoreReference];
            if (!rewardType) {
                return undefined;
            }

            return {
                text: interpolateDoubleBracesString(rewardType, {
                    value: this.getFormattedDecimalValue(reward.value, mapping.decimalDigits?.value),
                    currency: reward.currency,
                    maxCap: this.getFormattedDecimalValue(reward.maxCap, mapping.decimalDigits?.maxCap),
                }),
                cssClass: mapping.cssClass || '',
            };
        }

        return undefined;
    }

    private groupRewards(rewards: SignPostingReward[]): SignPostingReward[] {
        let index = 0;
        const rewardGroups = groupBy(rewards, (reward) => this.getGroupKey(reward, ++index));

        return keys(rewardGroups)
            .map((rewardGroup) => rewardGroups[rewardGroup])
            .map((r) => {
                return {
                    type: r[0].type,
                    value: sumBy(r, (reward) => reward.value),
                    currency: r[0].currency,
                    maxCap: r[0].maxCap,
                    rewardTime: r[0].rewardTime,
                    offerId: r[0].offerId,
                    offerType: r[0].offerType,
                };
            });
    }

    private getGroupKey(reward: SignPostingReward, index: number): string {
        const rewardType = reward.type.toLowerCase();
        const mapping = this.crmConfig.signposting.awardTypeMapping[rewardType];

        return mapping && mapping.grouping ? `${rewardType}|${reward.currency || ''}|${reward.maxCap || 0}` : `${rewardType}|${index}}`;
    }

    private getFormattedDecimalValue(decimalValue: number | undefined, decimalDigits: number | undefined): string {
        return decimalValue?.toFixed(decimalDigits || 0) || '';
    }
}
